import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import BiDashboard from "@/components/reports/BiDashboard";
import BiReportBuilder from "@/components/reports/BiReportBuilder";
import BiTemplates from "@/components/reports/BiTemplates";
import BiSchedules from "@/components/reports/BiSchedules";
import RecordExplorer from "@/components/reports/RecordExplorer";

export default function SmartReportsPage() {
  const [tab, setTab] = useState("dashboard");
  const [schema, setSchema] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [builderTemplate, setBuilderTemplate] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bi/templates`, { withCredentials: true });
      setTemplates(data.templates || []);
    } catch (error) {
      console.error("Templates failed to load", error);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/bi/schema`, { withCredentials: true })
      .then(({ data }) => setSchema(data))
      .catch((error) => {
        console.error("BI schema failed", error);
        toast.error("Could not load the reporting schema");
      });
    loadTemplates();
  }, [loadTemplates]);

  const openInBuilder = (template) => {
    setBuilderTemplate(template);
    setTab("builder");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900" data-testid="smart-reports-page">
      <div className="px-4 sm:px-6 pt-6 pb-2">
        <h1 className="text-2xl font-bold tracking-tight">SmartReports</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Business intelligence across referrals, visits, doctors, pads and rep activity.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="px-4 sm:px-6 pb-8">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1" data-testid="bi-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="builder" data-testid="tab-builder">
            Report Builder
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            Templates
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            Scheduled
          </TabsTrigger>
          <TabsTrigger value="explorer" data-testid="tab-explorer">
            Record Explorer
          </TabsTrigger>
        </TabsList>

        {!schema ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <TabsContent value="dashboard" className="mt-5">
              <BiDashboard presets={schema.date_presets} />
            </TabsContent>

            <TabsContent value="builder" className="mt-5">
              <BiReportBuilder
                key={builderTemplate?.template_id || "blank"}
                schema={schema}
                initialConfig={builderTemplate}
                onTemplateSaved={loadTemplates}
              />
            </TabsContent>

            <TabsContent value="templates" className="mt-5">
              <BiTemplates templates={templates} reload={loadTemplates} onEdit={openInBuilder} />
            </TabsContent>

            <TabsContent value="scheduled" className="mt-5">
              <BiSchedules />
            </TabsContent>
          </>
        )}

        <TabsContent value="explorer" className="mt-5 -mx-4 sm:-mx-6">
          <RecordExplorer />
        </TabsContent>
      </Tabs>
    </div>
  );
}
